/**
 * Notification center — full-page list of all notifications grouped by date,
 * with mark-read / dismiss / mark-all-read controls.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Bell, CheckCheck } from "lucide-react";

import { Button, Card, KpiCard, toast } from "@bizop/ui-kit";

import { PageHeader } from "../../components/PageHeader";
import { mockNotifications, type MockNotification } from "../../lib/notificationsMock";
import { useCurrentRole } from "../../lib/permissions";
import { usePersistentMockState } from "../../lib/usePersistentMockState";
import { NotificationItem } from "../../components/notifications/NotificationItem";

export const Route = createFileRoute("/_app/notifications")({
  component: NotificationsPage,
});

const DAY = 24 * 60 * 60 * 1000;

function NotificationsPage() {
  // Dual-backed: localStorage in mock mode, InstantDB when configured.
  // Sharing the same storeKey as the topbar bell keeps both views in sync.
  const [items, setItems] = usePersistentMockState<MockNotification[]>(
    "notifications",
    mockNotifications,
  );

  const currentRole = useCurrentRole();

  const roleFiltered = useMemo(
    () =>
      items.filter(
        (n) => n.targetRoles === null || n.targetRoles === undefined || n.targetRoles.includes(currentRole),
      ),
    [items, currentRole],
  );

  const grouped = useMemo(() => groupByDay(roleFiltered), [roleFiltered]);
  const unreadCount = roleFiltered.filter((n) => !n.isRead).length;

  function markRead(id: string) {
    setItems((prev) =>
      prev.map((n) => (n._id === id ? { ...n, isRead: true, readAt: Date.now() } : n)),
    );
  }
  function dismiss(id: string) {
    setItems((prev) => prev.filter((n) => n._id !== id));
    toast.success("Notification dismissed");
  }
  function markAllRead() {
    const now = Date.now();
    setItems((prev) =>
      prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt: now })),
    );
    toast.success("All notifications marked read");
  }

  // KPIs
  const byKind = useMemo(() => {
    const map: Record<string, number> = {};
    for (const n of roleFiltered) map[n.kind] = (map[n.kind] ?? 0) + 1;
    return map;
  }, [roleFiltered]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Notifications"
        description="Approvals, alerts, reminders, and system updates."
        actions={
          unreadCount > 0 ? (
            <Button variant="outline" onClick={markAllRead}>
              <CheckCheck className="h-4 w-4" /> Mark all read
            </Button>
          ) : undefined
        }
      />

      <section
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
        aria-label="Notification summary"
      >
        <KpiCard caption="Total" value={roleFiltered.length} />
        <KpiCard caption="Unread" value={unreadCount} hint="Across all kinds" />
        <KpiCard caption="Approvals" value={byKind.approval_required ?? 0} />
        <KpiCard caption="Alerts" value={byKind.alert ?? 0} />
      </section>

      {roleFiltered.length === 0 ? (
        <Card variant="feature">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Bell className="h-8 w-8 text-(--text-tertiary)" />
            <div className="text-[14px] font-medium text-(--text-primary)">
              No notifications yet
            </div>
            <div className="text-[12px] text-(--text-tertiary) max-w-md">
              When approvals, alerts, or reminders need your attention, they'll appear here.
            </div>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {grouped.map((g) => (
            <section key={g.label} className="flex flex-col gap-2">
              <h2 className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) px-1">
                {g.label}
              </h2>
              <Card variant="default" className="p-0">
                <ul className="divide-y divide-(--border-subtle)">
                  {g.items.map((n) => (
                    <li key={n._id}>
                      <NotificationItem
                        notification={n}
                        onMarkRead={markRead}
                        onDismiss={dismiss}
                        onActivate={(notif) => {
                          if (!notif.actionUrl) {
                            toast.info(notif.title);
                          }
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function groupByDay(items: MockNotification[]): Array<{ label: string; items: MockNotification[] }> {
  const buckets = new Map<string, { label: string; sortKey: number; items: MockNotification[] }>();
  const now = Date.now();
  for (const n of items) {
    const ageDays = Math.floor((now - n.createdAt) / DAY);
    let key: string;
    let label: string;
    let sortKey: number;
    if (ageDays === 0) {
      key = "today";
      label = "Today";
      sortKey = 0;
    } else if (ageDays === 1) {
      key = "yesterday";
      label = "Yesterday";
      sortKey = 1;
    } else if (ageDays < 7) {
      key = "thisWeek";
      label = "Earlier this week";
      sortKey = 2;
    } else if (ageDays < 30) {
      key = "thisMonth";
      label = "Earlier this month";
      sortKey = 3;
    } else {
      key = "older";
      label = "Older";
      sortKey = 4;
    }
    const existing = buckets.get(key);
    if (existing) {
      existing.items.push(n);
    } else {
      buckets.set(key, { label, sortKey, items: [n] });
    }
  }
  return Array.from(buckets.values())
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((b) => ({ label: b.label, items: b.items.sort((x, y) => y.createdAt - x.createdAt) }));
}
